import React from 'react';
import { TakeoverId, useTakeover } from '../../../hooks/useTakeover';
import RewardsTakeover from '../../../pages/ProductDetailPage/ProductOverview/components/RewardsTakeover';
import BadgeDetailTakeover from '../../Badge/BadgeDetailTakeover/BadgeDetailTakeover';
import BadgeEarnedTakeover from '../../Badge/BadgeEarnedTakeover/BadgeEarnedTakeover';
import ConnectSolarSystemTakeover from '../../ConnectSolarSystem/ConnectSolarSystemTakeover/ConnectSolarSystemTakeover';
import ConnectUtilityTakeover from '../../ConnectUtility/ConnectUtilityTakeover/ConnectUtilityTakeover';
import OnboardingCompletionTakeover from '../../OnboardingCompletionTakeover/OnboardingCompletionTakeover';
import { TakeoverContextProvider } from './TakeoverContext';

// Mapping of takeover ids to their components
// finalizing-verification and one-time-payment are lazy so their heavy step trees stay out of the startup bundle
const TAKEOVER_COMPONENT_MAP: Record<TakeoverId, React.ComponentType<any>> = {
  'utility-connect': ConnectUtilityTakeover,
  'badge-detail': BadgeDetailTakeover,
  'badge-earned': BadgeEarnedTakeover,
  'solar-connect': ConnectSolarSystemTakeover,
  rewards: RewardsTakeover,
  'onboarding-completion': OnboardingCompletionTakeover,
  'finalizing-verification': React.lazy(
    () => import('../../FinalizingVerificationTakeover/FinalizingVerificationTakeover'),
  ),
  'one-time-payment': React.lazy(
    () =>
      import(
        '../../../pages/Billing/components/SabalOneTimePaymentTakeover/DeepLink/SabalOneTimePaymentTakeoverDeepLink'
      ),
  ),
};

export const TakeoverProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { takeoverState, openTakeover, closeTakeover, closeAllTakeovers } = useTakeover();

  const renderTakeover = () => {
    if (!takeoverState.isOpen || !takeoverState.current) {
      return null;
    }

    const TakeoverComponent = TAKEOVER_COMPONENT_MAP[takeoverState.current.id];

    if (!TakeoverComponent) {
      return null;
    }

    return (
      <React.Suspense fallback={null}>
        <TakeoverComponent
          isOpen={takeoverState.isOpen}
          onDismiss={closeTakeover}
          {...takeoverState.current.props}
        />
      </React.Suspense>
    );
  };

  return (
    <TakeoverContextProvider
      openTakeover={openTakeover}
      closeTakeover={closeTakeover}
      closeAllTakeovers={closeAllTakeovers}
      takeoverState={takeoverState}
    >
      {children}
      {renderTakeover()}
    </TakeoverContextProvider>
  );
};
